import {
  AccountMerchantRef,
  TpvMerchant,
  TpvMerchantDetail,
} from '@app/domain/models/tpv/merchant.model';
import {
  AccountMerchantDto,
  MerchantBankDto,
  MerchantContactDto,
  MerchantDetailDto,
  MerchantDto,
} from '@app/data/dtos/tpv/merchant.dto';

/**
 * UUID del comercio. La fachada debería enviar `external_id_merchant`, pero
 * toleramos variantes para no perder el id si el backend lo nombra distinto
 * (sin el UUID, las lecturas por-comercio son imposibles). Confirmar el campo
 * real con backend y dejar solo el correcto (TPV_MIGRATION.md §9).
 */
function resolveMerchantUuid(dto: Record<string, unknown>): string {
  const candidates = [
    'external_id_merchant',
    'externalIdMerchant',
    // La fachada de `GET /merchants/` envía el UUID en `idMerchant` (string),
    // pese al nombre legacy que sugería numérico.
    'idMerchant',
    'id_merchant',
    'external_id',
    'merchant_id',
    'uuid',
  ];
  for (const key of candidates) {
    const v = dto[key];
    if (typeof v === 'string' && v.trim()) return v;
  }
  return '';
}

/** DTO de comercio -> entidad de dominio (id = UUID). */
export function mapMerchant(dto: MerchantDto): TpvMerchant {
  return {
    externalIdMerchant: resolveMerchantUuid(dto as unknown as Record<string, unknown>),
    name: dto.name ?? '',
    legalName: dto.social_name ?? '',
    rfc: dto.rfc,
    status: dto.status ?? '',
    unitName: dto.unit_name,
    entity: dto.entity,
    companyName: dto.company_name,
    orgIdCompany: dto.org_id_company,
    affiliationNumber: dto.affiliation_number,
    subaffiliationNumber: dto.subaffiliation_number,
  };
}

/** DTO de comercio-por-cuenta -> referencia de dominio. */
export function mapAccountMerchant(dto: AccountMerchantDto): AccountMerchantRef {
  return {
    externalIdMerchant: dto.external_id_merchant,
    status: dto.status ?? '',
  };
}

/** Toma el primer elemento si el bloque viene como array; tolera objeto pelado. */
function firstBlock<T>(block: T | T[] | undefined): T | undefined {
  if (Array.isArray(block)) return block[0];
  return block ?? undefined;
}

/** DTO del detalle de comercio -> entidad de dominio (id = UUID).
 *  Tolera la forma anidada (`extra_data.detail`) y el contacto como
 *  `contactPerson` (camelCase) o `contact_person`, igual que empresa/unidad. */
export function mapMerchantDetail(dto: MerchantDetailDto): TpvMerchantDetail {
  const d: MerchantDetailDto = dto.detail ?? dto;
  const addr = firstBlock(d.address);
  const contact: MerchantContactDto | undefined = firstBlock(d.contactPerson ?? d.contact_person);
  const bank: MerchantBankDto | undefined = firstBlock(d.bank);

  return {
    externalIdMerchant: resolveMerchantUuid(d as unknown as Record<string, unknown>),
    // Opcional hasta que backend lo exponga; habilita migrar el listado de
    // operadores por-comercio a la fachada (ver petición a backend).
    orgIdCompany: d.org_id_company,
    name: d.name ?? '',
    legalName: d.social_name ?? '',
    rfc: d.rfc,
    status: d.status ?? '',
    unitName: d.unit_name,
    idUnit: d.id_unit,
    affiliationNumber: d.affiliation_number,
    subaffiliationNumber: d.subaffiliation_number,
    entity: d.entity,
    represent: d.represent,
    url: d.url ?? null,
    returnToken: d.return_token,
    markupDebit: d.markup_merchant_debit ?? null,
    markupCredit: d.markup_merchant_credit ?? null,
    markupInternacional: d.markup_merchant_internacional ?? null,
    markupAmex: d.markup_merchant_amex ?? null,
    minAmount: d.min_amount ?? null,
    maxAmount: d.max_amount ?? null,
    msi3MinAmount: d.msi3_min_amount ?? null,
    msi3Agregator: d.msi3_agregator ?? null,
    msi3Bank: d.msi3_bank ?? null,
    msi6MinAmount: d.msi6_min_amount ?? null,
    msi6Agregator: d.msi6_agregator ?? null,
    msi6Bank: d.msi6_bank ?? null,
    msi9MinAmount: d.msi9_min_amount ?? null,
    msi9Agregator: d.msi9_agregator ?? null,
    msi9Bank: d.msi9_bank ?? null,
    msi12MinAmount: d.msi12_min_amount ?? null,
    msi12Agregator: d.msi12_agregator ?? null,
    msi12Bank: d.msi12_bank ?? null,
    msi18MinAmount: d.msi18_min_amount ?? null,
    msi18Agregator: d.msi18_agregator ?? null,
    msi18Bank: d.msi18_bank ?? null,
    address: addr
      ? {
          country: addr.country,
          state: addr.state,
          city: addr.city,
          town: addr.town,
          neighborhood: addr.neighborhood,
          cp: addr.cp,
          street: addr.street,
          extNumber: addr.ext_number,
          intNumber: addr.int_number,
          description: addr.description,
        }
      : undefined,
    contact: contact
      ? {
          name: contact.name,
          rol: contact.rol,
          email: contact.email,
          telephonePrincipal: contact.telephone_principal,
          telephoneSecond: contact.telephone_second,
          phoneMobile: contact.phone_mobile,
        }
      : undefined,
    bank: bank
      ? {
          bank: bank.bank,
          card: bank.card,
          numAccount: bank.num_account,
          clabe: bank.clabe,
        }
      : undefined,
  };
}
